import React, { useState } from 'react';
import { motion, AnimatePresence } from "motion/react";
import { MediaRenderer } from './ImagePlaceholder';
import type { GallerySliderProps } from '@/features/artists/types/types';

/**
 * Gallery slider component for artist media
 */
export const GallerySlider: React.FC<GallerySliderProps> = ({ galleryMedia }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % galleryMedia.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + galleryMedia.length) % galleryMedia.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  if (galleryMedia.length === 0) return null;

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Main Image Display */}
      <div className="relative aspect-video rounded-2xl overflow-hidden shadow-lg bg-gray-900">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="h-full w-full"
          >
            <MediaRenderer 
              mediaId={galleryMedia[currentIndex].media_id} 
              alt={`Gallery image ${currentIndex + 1}`}
              className="!rounded-2xl h-full w-full object-cover transition-transform duration-300 hover:scale-105" 
            />
          </motion.div>
        </AnimatePresence>

        {/* Navigation Arrows */}
        <AnimatePresence>
          {isHovered && galleryMedia.length > 1 && (
            <>
              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m15 18-6-6 6-6"/>
                </svg>
              </motion.button>
              <motion.button
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m9 18 6-6-6-6"/>
                </svg>
              </motion.button>
            </>
          )}
        </AnimatePresence>
      </div>

      {/* Thumbnail Navigation */}
      {galleryMedia.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {galleryMedia.map((item, index) => (
            <button
              key={item.media_id}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-200 ${
                index === currentIndex 
                  ? 'bg-blue-500 scale-125' 
                  : 'bg-gray-600 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      )}

      {/* Image Counter */}
      {galleryMedia.length > 1 && (
        <div className="absolute top-4 right-4 bg-black/50 text-white text-sm px-3 py-1 rounded-full">
          {currentIndex + 1} / {galleryMedia.length}
        </div>
      )}
    </div>
  );
};
